/**
 * PersonelSecici.jsx — bir firmanın GÜNCEL personel listesinden (Daimî Liste, `person_to_firma` aktif)
 * bir ya da birden çok kişi seçtiren liste. Backlog #142 (paylaşımlı belgeye çalışan seç) ve #145
 * (belgeyi başka kişiye taşı — `tekli`).
 *
 * Neden güncel liste: paylaşımlı belge ve belge taşıma yalnızca firmanın aktif kadrosundakilere açılır;
 * backend dışındakini 400 ile reddeder, burada baştan sunulmuyor. Liste uç: services/personelRosterService
 * `getFirmaRoster` (düz liste okuması; sekmeli liste de aynı uçtan, `?sekme=` ile). Seçim sayfalar arasında korunur.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Spinner, Table } from "reactstrap";
import { Search } from "lucide-react";
import { getFirmaRoster, ROSTER_AKTIF } from "../../services/personelRosterService";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;
const TUMUNU_SEC_SAYFA = 100;
const TUMUNU_SEC_UST_SINIR = 50; // sayfa

export default function PersonelSecici({
  firmaId,
  secili = [],
  onChange,
  tekli = false,
  devreDisi = [],
  devreDisiNot = "Belgede",
  haricKisiId = null,
}) {
  const { t } = useTranslation("components");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ records: [], total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hepsiYukleniyor, setHepsiYukleniyor] = useState(false);

  useEffect(() => {
    if (!firmaId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const rows = await getFirmaRoster(firmaId, { aktif: ROSTER_AKTIF.MEVCUT, search, page, size: PAGE_SIZE });
        if (!ignore) setData(rows);
      } catch (e) {
        if (!ignore) {
          setError(e.message);
          setData({ records: [], total: 0, totalPages: 1 });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [firmaId, search, page]);

  const seciliSet = new Set(secili.map(Number));
  const devreDisiSet = new Set(devreDisi.map(Number));
  const secilebilir = (row) => !devreDisiSet.has(Number(row.personId)) && Number(row.personId) !== Number(haricKisiId);
  const gorunen = data.records.filter((r) => Number(r.personId) !== Number(haricKisiId));
  const sayfadakiler = gorunen.filter(secilebilir);
  const sayfaTamam = sayfadakiler.length > 0 && sayfadakiler.every((r) => seciliSet.has(Number(r.personId)));

  function toggle(personId) {
    const id = Number(personId);
    if (tekli) {
      onChange(seciliSet.has(id) ? [] : [id]);
      return;
    }
    const next = new Set(seciliSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  }

  function sayfayiSec() {
    const next = new Set(seciliSet);
    sayfadakiler.forEach((r) => (sayfaTamam ? next.delete(Number(r.personId)) : next.add(Number(r.personId))));
    onChange([...next]);
  }

  // Aramaya uyan HERKESİ (tüm sayfalar) seçer — yüzlerce kişilik kadroda sayfa sayfa gezmemek için.
  async function hepsiniSec() {
    setHepsiYukleniyor(true);
    setError(null);
    try {
      const next = new Set(seciliSet);
      let p = 1;
      let toplamSayfa = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const rows = await getFirmaRoster(firmaId, { aktif: ROSTER_AKTIF.MEVCUT, search, page: p, size: TUMUNU_SEC_SAYFA });
        toplamSayfa = rows.totalPages || 1;
        rows.records
          .filter((r) => secilebilir(r))
          .forEach((r) => next.add(Number(r.personId)));
        p += 1;
      } while (p <= toplamSayfa && p <= TUMUNU_SEC_UST_SINIR);
      onChange([...next]);
    } catch (e) {
      setError(e.message);
    } finally {
      setHepsiYukleniyor(false);
    }
  }

  const totalPages = Math.max(1, data.totalPages || 1);

  return (
    <div>
      <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
        <div className="d-flex">
          <Input
            bsSize="sm" value={searchInput} placeholder={t("personelSecici.adSoyadAra")} style={{ minWidth: 180 }}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setSearch(searchInput); setPage(1); } }}
          />
          <Button size="sm" color="light" onClick={() => { setSearch(searchInput); setPage(1); }}>
            <Search size={14} />
          </Button>
        </div>
        {!tekli && (
          <Button size="sm" color="light" onClick={hepsiniSec} disabled={hepsiYukleniyor || loading || data.total === 0}>
            {hepsiYukleniyor ? <Spinner size="sm" /> : t("personelSecici.tumunuSec", { total: data.total })}
          </Button>
        )}
        {!tekli && seciliSet.size > 0 && (
          <Button size="sm" color="light" onClick={() => onChange([])}>{t("personelSecici.secimiTemizle")}</Button>
        )}
        <span className="small text-muted ms-auto">{seciliSet.size}{" "}{t("personelSecici.secili")}</span>
      </div>

      {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}

      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : gorunen.length === 0 ? (
        <div className="text-center py-4 text-muted small">{t("personelSecici.guncelPersonelListesindeKayitYok")}</div>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 34 }}>
                  {!tekli && (
                    <Input
                      type="checkbox" checked={sayfaTamam} onChange={sayfayiSec}
                      disabled={sayfadakiler.length === 0} aria-label={t("personelSecici.buSayfadakilerinTumunuSec")}
                    />
                  )}
                </th>
                <th>{t("personelSecici.kisi")}</th>
                <th>{t("personelSecici.kimlikNo")}</th>
              </tr>
            </thead>
            <tbody>
              {gorunen.map((row) => {
                const id = Number(row.personId);
                const kapali = devreDisiSet.has(id);
                return (
                  <tr
                    key={row.personId}
                    style={{ cursor: kapali ? "default" : "pointer", opacity: kapali ? 0.55 : 1 }}
                    onClick={kapali ? undefined : () => toggle(id)}
                  >
                    <td>
                      <Input
                        type={tekli ? "radio" : "checkbox"} checked={seciliSet.has(id)} disabled={kapali}
                        onChange={() => toggle(id)} onClick={(e) => e.stopPropagation()}
                        aria-label={`${row.ad} ${row.soyad}`}
                      />
                    </td>
                    <td>
                      {row.ad} {row.soyad}
                      {kapali && <span className="small text-muted ms-2">{devreDisiNot}</span>}
                    </td>
                    <td className="font-monospace small">{row.tcNo || row.kimlikNo || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
          <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</Button>
          <span className="small text-muted">{page} / {totalPages}</span>
          <Button size="sm" color="light" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</Button>
        </div>
      )}
    </div>
  );
}
